<template>
  <auth-card title="Créer un compte" subtitle="Suivez vos sections favorites et accédez à votre espace membre.">
    <v-alert class="mb-4" color="primary" icon="mdi-link-variant" variant="tonal" density="compact">
      Abonné sur l'ancienne plateforme? Utilisez la même adresse courriel pour lier votre abonnement.
    </v-alert>
    <v-form ref="form" v-model="valid" @submit="submitAction" :disabled="isLoading || showSubscriptionPrompt">
      <v-text-field
        v-model="displayName"
        :counter="50"
        :rules="displayNameRules"
        label="Votre nom (affiché à l'accueil)"
        autocomplete="name"
        prepend-inner-icon="mdi-account-outline"
        class="mb-2"
        required
        @focus="clearErrorsOnFocus"
      ></v-text-field>
      <v-text-field
        v-model="email"
        :rules="emailRules"
        label="Courriel (identifiant)"
        type="email"
        autocomplete="email"
        prepend-inner-icon="mdi-email-outline"
        class="mb-2"
        required
        @focus="clearErrorsOnFocus"
      ></v-text-field>
      <v-text-field
        v-model="password"
        :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
        :rules="passwordRules"
        label="Mot de passe"
        :type="showPassword ? 'text' : 'password'"
        name="password"
        autocomplete="new-password"
        hint="Au minimum 8 caractères"
        prepend-inner-icon="mdi-lock-outline"
        class="mb-2"
        @click:append-inner="showPassword = !showPassword"
        @focus="clearErrorsOnFocus"
      ></v-text-field>
      <v-text-field
        v-model="passwordValidation"
        :append-inner-icon="showPasswordValidation ? 'mdi-eye' : 'mdi-eye-off'"
        :rules="passwordValidationRules"
        label="Confirmation du mot de passe"
        :type="showPasswordValidation ? 'text' : 'password'"
        name="passwordValidation"
        autocomplete="new-password"
        prepend-inner-icon="mdi-lock-check-outline"
        class="mb-2"
        :success="passwordMatch()"
        @click:append-inner="showPasswordValidation = !showPasswordValidation"
        @focus="clearErrorsOnFocus"
      ></v-text-field>
      <v-alert v-if="errorMessage" class="mb-4" type="error" variant="tonal" density="compact">
        {{ errorMessage }}
      </v-alert>
      <v-alert v-if="isLoading" class="mb-4" icon="mdi-timer-sand" variant="tonal" type="info" density="compact">
        Veuillez patienter pendant la création de votre compte.
      </v-alert>
      <!-- Sign-up: the screen's one gold call-to-action (docs/BRAND.md) -->
      <v-btn
        :disabled="!valid || isLoading || showSubscriptionPrompt"
        :loading="isLoading"
        color="gold"
        variant="flat"
        size="large"
        block
        type="submit"
      >
        Créer un compte
      </v-btn>
      <div class="d-flex justify-center mt-2">
        <v-btn
          variant="text"
          size="small"
          color="medium-emphasis"
          :disabled="isLoading || showSubscriptionPrompt"
          @click="clearForm"
        >
          Effacer le formulaire
        </v-btn>
      </div>
    </v-form>
    <v-dialog v-model="showSubscriptionPrompt" persistent max-width="600">
      <v-card>
        <v-card-title class="text-headline-small">Souhaitez-vous vous abonner ?</v-card-title>
        <v-card-text>
          Vous pouvez vous abonner maintenant pour accéder aux fonctionnalités premium, ou ignorer et revenir à
          l'accueil.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" :loading="isLoading" :disabled="isLoading" @click="onSkip">Passer</v-btn>
          <v-btn color="gold" variant="flat" :loading="isLoading" :disabled="isLoading" @click="onSubscribe">
            S'abonner
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showMembershipConfirmed" max-width="500">
      <v-card>
        <v-card-title class="text-headline-small">Abonnement actif</v-card-title>
        <v-card-text>
          Nous avons détecté un abonnement actif pour votre compte. Appuyez sur OK pour revenir à l'accueil.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" :loading="isLoading" :disabled="isLoading" @click="onMembershipConfirmed">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <template #footer>
      Déjà un compte?
      <router-link :to="{ name: 'connexion' }" class="auth-link text-section-name font-weight-bold">Se connecter</router-link>
    </template>
  </auth-card>
</template>

<script>
  import { auth, db } from "@/firebase";
  import { UserService } from "@/store/modules/user";
  import AuthCard from "@/components/AuthCard.vue";

  export default {
    components: { AuthCard },
    data() {
      return {
        valid: true,
        displayName: "",
        displayNameRules: [
          (v) => !!v || "Un nom d'utilisateur est requis",
          (v) => (v && v.length > 2) || "Le nom d'utilisateur doit contenir au moins 3 caractères",
        ],
        email: "",
        emailRules: [
          (v) => !!v || "Un courriel est requis",
          (v) => /.+@.+\..+/.test(v) || "Le courriel doit être valide",
        ],
        password: "",
        passwordRules: [(v) => !!v || "Un mot de passe est requis", (v) => v.length >= 8 || "Au minimum 8 caractères"],
        passwordValidation: "",
        passwordValidationRules: [
          (v) => !!v || "Confirmez le mot de passe",
          (v) => v === this.password || "Le mot de passe ne correspond pas",
        ],
        showPassword: false,
        showPasswordValidation: false,
        errorMessage: "",
        userMembershipInfo: { id: null, access: "aucun", endDate: null, note: "" },
        isLoading: false,
        showSubscriptionPrompt: false,
        showMembershipConfirmed: false,
      };
    },
    methods: {
      // Empties the fields and clears their error messages
      clearForm() {
        this.$refs.form.reset();
        this.$refs.form.resetValidation();
      },
      passwordMatch() {
        return this.password === this.passwordValidation && this.password !== "" ? true : false;
      },
      async submitAction(evt) {
        evt.preventDefault();
        if (this.isLoading) return;
        this.isLoading = true;
        this.errorMessage = "";

        try {
          const userCredential = await auth.createUserWithEmailAndPassword(this.email, this.password);
          if (userCredential) {
            // Ensure a default userRole exists immediately so other listeners won't hit the missing-doc path.
            try {
              const uid = (userCredential.user && userCredential.user.uid) || auth.currentUser.uid;
              await db
                .collection("userRole")
                .doc(uid)
                .set({ id: uid, access: "aucun", endDate: null, note: "" }, { merge: true });
            } catch (e) {
              console.warn("Could not create default userRole doc:", e);
            }

            // Update profile after ensuring the default role doc exists
            await auth.currentUser.updateProfile({ displayName: this.displayName });

            // After account creation, try to sync membership. Do not open subscription dialog until check completes.
            try {
              this.isLoading = true;
              // Call centralized membership sync on UserService
              let isMember = false;
              try {
                const res = await UserService.actions.syncMembershipForUser(auth.currentUser);
                if (res && res.isMember) isMember = true;
              } catch (e) {
                console.warn("UserService.syncMembershipForUser failed, falling back to local check", e);
                isMember = false;
              }

              if (isMember) {
                this.showMembershipConfirmed = true;
              } else {
                this.showSubscriptionPrompt = true;
              }
            } finally {
              this.isLoading = false;
            }
          }
        } catch (error) {
          if (error && error.code === "auth/too-many-requests") {
            this.errorMessage =
              "Trop de tentatives de création de compte. Veuillez patienter quelques minutes avant de réessayer.";
          } else if (error && error.code === "auth/email-already-in-use") {
            this.errorMessage = "Un utilisateur est déjà existant avec cette adresse courriel";
          } else {
            this.errorMessage = (error && error.message) || "Erreur lors de la création du compte";
          }
        } finally {
          this.isLoading = false;
        }
      },
      async onSubscribe() {
        if (this.isLoading) return;
        this.isLoading = true;
        try {
          await this.$router.push({ name: "subscription" });
        } catch (e) {
          console.error(e);
        } finally {
          this.isLoading = false;
        }
      },
      async onSkip() {
        if (this.isLoading) return;
        this.isLoading = true;
        try {
          await this.$router.push({ name: "accueil" });
        } catch (e) {
          console.error(e);
        } finally {
          this.isLoading = false;
        }
      },
      async onMembershipConfirmed() {
        if (this.isLoading) return;
        this.isLoading = true;
        this.showMembershipConfirmed = false;
        try {
          await this.$router.push({ name: "accueil" });
        } catch (e) {
          console.error(e);
        } finally {
          this.isLoading = false;
        }
      },

      initMessageError() {
        this.errorMessage = "";
      },
      clearErrorsOnFocus() {
        this.initMessageError();
        if (this.$refs.form) {
          this.$refs.form.resetValidation();
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .auth-link {
    text-decoration: none;
  }

  .auth-link:hover {
    text-decoration: underline;
  }
</style>
